import React, { useState } from "react";
import styled from "styled-components";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import DialogAddOrderAdmin from "./AdminContentDialog";
import { StationDocumentsSection } from "../StationDocuments/StationDocuments";
import { ADMIN_CONTENT_ITEMS, AdminContentItem } from "./contentConfig";
import PermissionGate from "../../controls/auth/PermissionGate";

/* ─── Layout ──────────────────────────────────────────────────── */

const Shell = styled(MyBookingWrapper)`
	padding: 20px 0 48px;
`;

const Inner = styled.div`
	width: 1200px;
	max-width: 100%;
	margin: 0 auto;

	@media (max-width: 1282px) {
		width: 100%;
		padding: 0 16px;
	}
`;

const PageHeader = styled.div`
	margin-bottom: 28px;
`;

const PageTitle = styled.h1`
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #111827;
`;

const PageMeta = styled.div`
	margin-top: 4px;
	font-size: 13px;
	color: #6b7280;
`;

/* ─── Section ─────────────────────────────────────────────────── */

const Section = styled.div`
	& + & {
		margin-top: 32px;
	}
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 14px;
`;

const SectionTitle = styled.div`
	font-size: 13px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	color: #6b7280;
	white-space: nowrap;
`;

const SectionLine = styled.div`
	flex: 1;
	height: 1px;
	background: #e5e7eb;
`;

/* ─── Grid ────────────────────────────────────────────────────── */

const Grid = styled.div<{ $cols: number }>`
	display: grid;
	grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
	gap: 14px;

	@media (max-width: 1024px) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

/* ─── Card ────────────────────────────────────────────────────── */

const Card = styled.div`
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 16px;
	padding: 18px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const CardTop = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

const CardIcon = styled.div<{ $bg: string; $color: string }>`
	width: 40px;
	height: 40px;
	border-radius: 10px;
	background: ${({ $bg }) => $bg};
	color: ${({ $color }) => $color};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	flex-shrink: 0;
`;

const CardInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

const CardName = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	line-height: 1.35;
`;

const CardDesc = styled.div`
	font-size: 13px;
	color: #6b7280;
	margin-top: 4px;
	line-height: 1.5;
`;

const CardDivider = styled.div`
	height: 1px;
	background: #f1f5f9;
`;

const EditBtn = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 7px 14px;
	border-radius: 10px;
	border: 1px solid #2563eb;
	background: #eff6ff;
	color: #2563eb;
	font-size: 13px;
	font-weight: 700;
	cursor: pointer;
	transition: background 0.15s;
	margin-left: auto;

	&:hover {
		background: #dbeafe;
	}
`;

/* ─── Divider between page sections ──────────────────────────── */

const PageDivider = styled.div`
	height: 1px;
	background: #e5e7eb;
	margin: 40px 0;
`;

/* ─── Data ────────────────────────────────────────────────────── */

const SECTIONS = [
	{ key: "Legal", cols: 4 },
	{ key: "Company", cols: 2 },
	{ key: "Partners", cols: 1 },
];

/* ─── Component ───────────────────────────────────────────────── */

export default function AdminContentManage() {
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogType, setDialogType] = useState("");

	const handleOpenDialog = (type: string) => {
		setDialogType(type);
		setOpenDialog(true);
	};

	return (
		<Shell>
			<Inner>
				<PageHeader>
					<PageTitle>Quản lý nội dung</PageTitle>
					<PageMeta>Chỉnh sửa nội dung trang web và tài liệu Station.</PageMeta>
				</PageHeader>

				{SECTIONS.map(({ key, cols }) => {
					const items = ADMIN_CONTENT_ITEMS.filter((c: AdminContentItem) => c.section === key);
					return (
						<Section key={key}>
							<SectionHeader>
								<SectionTitle>{key}</SectionTitle>
								<SectionLine />
							</SectionHeader>
							<Grid $cols={cols}>
								{items.map((item) => (
									<Card key={item.type}>
										<CardTop>
											<CardIcon
												$bg={item.iconBg}
												$color={item.iconColor}>
												<i className={item.icon} />
											</CardIcon>
											<CardInfo>
												<CardName>{item.label}</CardName>
												<CardDesc>{item.desc}</CardDesc>
											</CardInfo>
										</CardTop>
										<CardDivider />
										<PermissionGate permission="documents.manage">
											<EditBtn
												type="button"
												onClick={() => handleOpenDialog(item.type)}>
												<i className="fa fa-pencil" />
												Chỉnh sửa nội dung
											</EditBtn>
										</PermissionGate>
									</Card>
								))}
							</Grid>
						</Section>
					);
				})}
			</Inner>

			<DialogAddOrderAdmin
				openDialog={openDialog}
				setOpenDialog={setOpenDialog}
				dialogType={dialogType}
			/>

			<PageDivider />

			<StationDocumentsSection />
		</Shell>
	);
}
